"use client";

import {
  Button,
  Card,
  CardHeader,
  Input,
  Label,
} from "@fluxlogis/ui";
import type { CSSProperties } from "react";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { authClient } from "@/lib/auth-client";

const featureSlides = [
  {
    title: "Operación centralizada",
    description: "Gestiona equipos, procesos y actividad diaria desde una plataforma preparada para crecer.",
  },
  {
    title: "Visibilidad en tiempo real",
    description: "Consulta información clave para tomar decisiones operativas con más claridad.",
  },
  {
    title: "Control por roles",
    description: "Organiza accesos y responsabilidades para que cada persona trabaje en el contexto correcto.",
  },
];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;

    if (typeof message === "string") {
      return message;
    }
  }

  return "No pudimos completar la solicitud. Intenta nuevamente.";
}

function AuthSkeleton() {
  return (
    <main className="login-page">
      <section className="login-panel" aria-label="Cargando acceso">
        <div className="brand-block">
          <p className="eyebrow">FLUXLOGIS</p>
          <h1>Bienvenido</h1>
          <p>Ingresa a tu plataforma de gestión logística.</p>
        </div>
        <Card aria-label="Cargando sesión">
          <div className="skeleton skeleton-title" />
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-input" />
          <div className="skeleton skeleton-input" />
          <div className="skeleton skeleton-button" />
        </Card>
      </section>
    </main>
  );
}

function FeaturePanel() {
  return (
    <section className="feature-panel" aria-label="Beneficios de la plataforma">
      <div className="feature-carousel" aria-live="polite">
        {featureSlides.map((slide, index) => (
          <article className="feature-slide" key={slide.title} style={{ "--slide-index": index } as CSSProperties}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h2>{slide.title}</h2>
            <p>{slide.description}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function LoginScreen({
  email,
  feedback,
  isSubmitting,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  password,
}: {
  email: string;
  feedback: string | null;
  isSubmitting: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  password: string;
}) {
  return (
    <main className="login-page">
      <section className="login-panel" aria-label="Acceso a FLUXLOGIS">
        <div className="brand-block">
          <p className="eyebrow">FLUXLOGIS</p>
          <h1>Bienvenido</h1>
          <p>Ingresa a tu plataforma de gestión logística.</p>
        </div>

        <Card aria-labelledby="auth-title">
          <CardHeader>
            <div>
              <p className="eyebrow">Acceso privado</p>
              <h2 id="auth-title">Iniciar sesión</h2>
            </div>
          </CardHeader>

          <form className="auth-form" onSubmit={onSubmit}>
            <Label>
              Email
              <Input
                type="email"
                value={email}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onEmailChange(event.target.value)}
                autoComplete="email"
                required
              />
            </Label>

            <Label>
              Contraseña
              <Input
                type="password"
                value={password}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onPasswordChange(event.target.value)}
                autoComplete="current-password"
                required
              />
            </Label>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>

          <p className="auth-note">El acceso se gestiona por invitación o asignación administrativa.</p>

          {feedback ? <p className="form-feedback">{feedback}</p> : null}
        </Card>
      </section>

      <FeaturePanel />
    </main>
  );
}

function TwoFactorScreen({
  code,
  feedback,
  isBackupCodeMode,
  isSubmitting,
  onBack,
  onCodeChange,
  onSubmit,
  onToggleBackupMode,
}: {
  code: string;
  feedback: string | null;
  isBackupCodeMode: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onCodeChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onToggleBackupMode: () => void;
}) {
  return (
    <main className="login-page">
      <section className="login-panel" aria-label="Verificación de dos pasos">
        <div className="brand-block">
          <p className="eyebrow">FLUXLOGIS</p>
          <h1>Seguridad 2FA</h1>
          <p>Se requiere un código de autenticación para completar el acceso.</p>
        </div>

        <Card aria-labelledby="auth-2fa-title">
          <CardHeader>
            <div>
              <p className="eyebrow">Doble factor</p>
              <h2 id="auth-2fa-title">
                {isBackupCodeMode ? "Código de respaldo" : "Código del autenticador"}
              </h2>
            </div>
          </CardHeader>

          <form className="auth-form" onSubmit={onSubmit}>
            <Label>
              {isBackupCodeMode ? "Código de respaldo" : "Código de 6 dígitos"}
              <Input
                autoComplete="one-time-code"
                autoFocus
                maxLength={isBackupCodeMode ? 20 : 6}
                onChange={(event: ChangeEvent<HTMLInputElement>) => onCodeChange(event.target.value)}
                placeholder={isBackupCodeMode ? "xxxxxxxx-xxxx" : "123456"}
                required
                style={{
                  fontSize: isBackupCodeMode ? "1rem" : "1.5rem",
                  letterSpacing: isBackupCodeMode ? "normal" : "0.25rem",
                  textAlign: "center",
                }}
                type="text"
                value={code}
              />
            </Label>

            <Button disabled={isSubmitting || !code.trim()} type="submit">
              {isSubmitting ? "Verificando..." : "Verificar y entrar"}
            </Button>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginTop: "0.5rem" }}>
              <button
                className="profile-link-btn"
                onClick={onToggleBackupMode}
                style={{ background: "none", border: "none", color: "var(--flx-brand)", cursor: "pointer", fontSize: "0.85rem", textAlign: "center" }}
                type="button"
              >
                {isBackupCodeMode ? "Usar app de autenticación (TOTP)" : "¿No tienes acceso a tu app? Usa un código de respaldo"}
              </button>

              <button
                className="profile-link-btn"
                onClick={onBack}
                style={{ background: "none", border: "none", color: "var(--flx-text-muted)", cursor: "pointer", fontSize: "0.85rem", textAlign: "center" }}
                type="button"
              >
                ← Volver al login
              </button>
            </div>
          </form>

          {feedback ? <p className="form-feedback">{feedback}</p> : null}
        </Card>
      </section>

      <FeaturePanel />
    </main>
  );
}

export function AuthPanel() {
  const router = useRouter();
  const { data: session, isPending, refetch } = authClient.useSession();
  const [email, setEmail] = useState("superadmin@fluxlogis.com");
  const [password, setPassword] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2FA state
  const [isTwoFactorStep, setIsTwoFactorStep] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [isBackupCodeMode, setIsBackupCodeMode] = useState(false);

  useEffect(() => {
    if (session?.user) {
      router.push("/dashboard");
    }
  }, [session?.user, router]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    setIsSubmitting(true);

    try {
      const result = await authClient.signIn.email({
        email,
        password,
      });

      if (result.error) {
        throw new Error(result.error.message ?? "No pudimos iniciar sesión.");
      }

      if ((result.data as { twoFactorRedirect?: boolean })?.twoFactorRedirect) {
        setIsTwoFactorStep(true);
        setFeedback("Introduce el código de 6 dígitos de tu app de autenticación.");
        return;
      }

      setFeedback("Sesión iniciada correctamente.");
      await refetch();
      router.push("/dashboard");
      router.refresh();
    } catch (error) {
      setFeedback(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTwoFactorSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);
    setIsSubmitting(true);

    try {
      const cleanCode = totpCode.trim();
      let res;
      if (isBackupCodeMode) {
        res = await authClient.twoFactor.verifyBackupCode({ code: cleanCode });
      } else {
        res = await authClient.twoFactor.verifyTotp({ code: cleanCode });
      }

      if (res.error) {
        throw new Error(res.error.message || "Código incorrecto o expirado.");
      }

      setFeedback("Verificación exitosa. Entrando...");
      await refetch();
      router.push("/dashboard");
      router.refresh();
    } catch (error) {
      setFeedback(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isPending || session?.user) {
    return <AuthSkeleton />;
  }

  if (isTwoFactorStep) {
    return (
      <TwoFactorScreen
        code={totpCode}
        feedback={feedback}
        isBackupCodeMode={isBackupCodeMode}
        isSubmitting={isSubmitting}
        onBack={() => {
          setIsTwoFactorStep(false);
          setTotpCode("");
          setFeedback(null);
        }}
        onCodeChange={setTotpCode}
        onSubmit={handleTwoFactorSubmit}
        onToggleBackupMode={() => {
          setIsBackupCodeMode(!isBackupCodeMode);
          setTotpCode("");
          setFeedback(null);
        }}
      />
    );
  }

  return (
    <LoginScreen
      email={email}
      feedback={feedback}
      isSubmitting={isSubmitting}
      onEmailChange={setEmail}
      onPasswordChange={setPassword}
      onSubmit={handleSubmit}
      password={password}
    />
  );
}
